import React, { ReactNode } from 'react';
import {
  Card,
  Grid,
  BoldSmallBodyText,
  SmallBodyText,
  SpaceWrapper,
  Currency,
  Icon,
  IconType,
  IconSize,
  FlexRow,
  NavigationItem,
  Header3Text,
  WorkflowHeader,
  Button,
  Footer,
  Notification,
  PageLoading,
  Layout,
  INavigationItemProps,
} from 'ui-toolkit';
import styled from 'styled-components';
import PremiumPrice from '../PremiumPrice';
import { IAmericanDigitalShortFormData } from './IAmericanDigitalShortFormData';
import { IAmericanDigitalShortFormActions } from './IAmericanDigitalShortFormActions';
import { PolicyEditButton } from '../PolicyEditButton';
import { propertyAddressToString } from '../../../ui-toolkit/orders/interfaces/IPropertyAddress';
import { DateFormat, getDateString } from '../../../ui-toolkit/utilities/getDateString';

const Grid2Column = styled(Grid)`
  grid-template-columns: 3fr 13fr;
  gap: ${(props) => props.theme.spacing.spacing4};
  padding-right: ${(props) => `${props.theme.spacing.defaultPaddingValue * 2}${props.theme.spacing.baseUnit}`};
`;

const Grid4Column = styled(Grid)`
  grid-template-columns: 3fr 5fr 3fr 5fr;
  gap: ${(props) => props.theme.spacing.spacing4};
`;

const EndorsementGrid = styled(Grid)`
  gap: ${(props) => props.theme.spacing.spacing4};
`;

const RequiredSmallBodyText = styled(SmallBodyText)`
  color: ${(props) => props.theme.colors.errorText};
`;

export interface IReviewPolicyDetailsProps {
  actions: IAmericanDigitalShortFormActions;
  data: IAmericanDigitalShortFormData;
}

interface IDataPointDisplayProps {
  label: string;
  hasValue: boolean;
  text: ReactNode;
  onAdd?: INavigationItemProps;
  onEdit?: INavigationItemProps;
}

const DataPointDisplay: React.FC<IDataPointDisplayProps> = ({ label, hasValue, text, onAdd, onEdit }) => {
  return (
    <>
      <BoldSmallBodyText>{label}</BoldSmallBodyText>
      <FlexRow>
        {hasValue ? <SmallBodyText>{text}</SmallBodyText> : <RequiredSmallBodyText>Required</RequiredSmallBodyText>}
        {!hasValue && onAdd ? (
          <SmallBodyText>
            <NavigationItem {...onAdd} isInline />
          </SmallBodyText>
        ) : null}
        {hasValue && onEdit ? (
          <SmallBodyText>
            <NavigationItem {...onEdit} isInline />
          </SmallBodyText>
        ) : null}
      </FlexRow>
    </>
  );
};

export const AmericanDigitalShortForm: React.FC<IReviewPolicyDetailsProps> = ({ actions, data }) => {
  const header = (
    <WorkflowHeader title={'Create Short Form Loan Policy'} labelBack={'Exit'} onBackClick={actions.cancel}>
      <Button intent={'primary'} onClick={actions.createPolicy} disabled={!data.isReadyToCreatePolicy}>
        Create Policy
      </Button>
    </WorkflowHeader>
  );
  const footer = (
    <Footer>
      <FlexRow justifyContent={'space-between'}>
        <Button intent="ghost" isInline onClick={actions.cancel}>
          Exit
        </Button>
        <Button intent={'primary'} onClick={actions.createPolicy} disabled={!data.isReadyToCreatePolicy}>
          Create Policy
        </Button>
      </FlexRow>
    </Footer>
  );
  return (
    <>
      <Layout header={header} footer={footer}>
        <SpaceWrapper>
          {data.notification.text ? (
            <SpaceWrapper>
              <Notification type={data.notification.type}>{data.notification.text}</Notification>
            </SpaceWrapper>
          ) : null}
          <Card title="Review Policy Details">
            <Header3Text>Schedule A</Header3Text>
            <SpaceWrapper leftSpace={'none'} rightSpace="none" bottomSpace={'default'} topSpace={'default'}>
              <Grid2Column>
                <DataPointDisplay label={`File No.`} text={data.file_number} hasValue />
                <DataPointDisplay
                  label={`Policy No.`}
                  text={data.policy_number}
                  hasValue={!!data.policy_number}
                  onAdd={actions.addPolicyNumber}
                />
                <DataPointDisplay
                  label={`Loan No.`}
                  text={data.loan_number}
                  hasValue={!!data.loan_number}
                  onAdd={actions.addLoanNumber}
                />
              </Grid2Column>
            </SpaceWrapper>
            <SpaceWrapper leftSpace={'none'} rightSpace="none" bottomSpace={'default'} topSpace={'default'}>
              <Grid2Column>
                <DataPointDisplay
                  label={`Property Address`}
                  text={propertyAddressToString(data.property_address)}
                  hasValue
                />
                <DataPointDisplay
                  label={`County`}
                  text={data.property_address.countyName}
                  hasValue={!!data.property_address.countyName}
                />
              </Grid2Column>
            </SpaceWrapper>
            <SpaceWrapper leftSpace={'none'} rightSpace="none" bottomSpace={'default'} topSpace={'default'}>
              <Grid4Column>
                <DataPointDisplay
                  label={`Amount of Insurance`}
                  text={<Currency value={data.amount_of_insurance} />}
                  hasValue={!!data.amount_of_insurance}
                  onAdd={actions.addAmountOfInsurance}
                />
                <DataPointDisplay
                  label={`Premium Amount`}
                  text={<PremiumPrice premium={data.premiums} />}
                  hasValue={!!(data.premiums && data.premiums.length === 1)}
                  onAdd={actions.editPremiumAmount}
                  onEdit={actions.editPremiumAmount}
                />
                <DataPointDisplay
                  label={`Mortgage Date`}
                  text={getDateString(DateFormat.long, data.mortgage_date)}
                  hasValue={!!data.mortgage_date}
                  onAdd={actions.addMortgageDate}
                  onEdit={actions.editMortgageDate}
                />
              </Grid4Column>
            </SpaceWrapper>
            <SpaceWrapper leftSpace={'none'} rightSpace="none" bottomSpace={'default'} topSpace={'default'}>
              <Grid2Column>
                <DataPointDisplay label={`Name of Insured`} text={data.name_of_insured} hasValue />
              </Grid2Column>
            </SpaceWrapper>
            <SpaceWrapper leftSpace={'none'} rightSpace="none" topSpace={'default'}>
              <Grid2Column>
                <DataPointDisplay
                  label={`Borrower Name(s)`}
                  text={data.borrower_full_names}
                  hasValue
                  onEdit={actions.editBorrowers}
                />
              </Grid2Column>
            </SpaceWrapper>
            <Header3Text>Endorsements</Header3Text>
            <EndorsementGrid>
              {data.endorsements.map((item, index) => (
                <FlexRow key={index}>
                  <Icon type={IconType.Check} size={IconSize.Small} />
                  <SmallBodyText>{item}</SmallBodyText>
                  <SmallBodyText>
                    <PolicyEditButton />
                  </SmallBodyText>
                </FlexRow>
              ))}
            </EndorsementGrid>
          </Card>
        </SpaceWrapper>
      </Layout>
      <PageLoading isLoading={data.isBusy} />
    </>
  );
};
